import React, { useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, Check, Truck, RefreshCcw, ShieldCheck, ArrowRight } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Reveal } from "@/components/Reveal";
import { COLORS, PRODUCT, IMG, CARE, FEATURES, FAQS } from "@/data/site";
import { useCart } from "@/lib/cart";
import { eur } from "@/lib/api";
import PaymentIcons from "@/components/PaymentIcons";
import Seo from "@/components/Seo";

function ZoomImage({ src, alt }) {
  const [zoom, setZoom] = useState(false);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const ref = useRef(null);
  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPos({ x, y });
  };
  return (
    <div
      ref={ref}
      data-testid="product-zoom"
      className="relative w-full h-full overflow-hidden cursor-zoom-in bg-ink"
      onMouseEnter={() => setZoom(true)}
      onMouseLeave={() => setZoom(false)}
      onMouseMove={onMove}
    >
      <img
        src={src}
        alt={alt}
        data-testid="product-main-image"
        className="w-full h-full object-cover transition-transform duration-200"
        style={{ transform: zoom ? "scale(1.9)" : "scale(1)", transformOrigin: `${pos.x}% ${pos.y}%` }}
      />
    </div>
  );
}

export default function Product() {
  const [params, setParams] = useSearchParams();
  const colorParam = params.get("color");
  const initialColor = COLORS.find((c) => c.key === colorParam) || COLORS[0];
  const [color, setColor] = useState(initialColor);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const { addItem, setOpen } = useCart();

  const gallery = useMemo(() => [color.img, IMG.rail, IMG.grip], [color]);

  const selectColor = (c) => {
    setColor(c);
    setActiveImg(0);
    setParams({ color: c.key }, { replace: true });
  };

  const add = () => addItem(color.key, color.label, qty);

  return (
    <div className="bg-ink pt-[70px] md:pt-[84px]">
      <Seo title="STARSOX Performance Grip Socks" description="Premium Grip-Socken mit Silikon-Grip-Pads für maximale Traktion. One Size (EU 39–46), 9,99 €. In Weiß, Neon Lime & Sunset Orange." path="/product" />
      <div className="container-x py-8 md:py-14">
        {/* breadcrumb */}
        <div className="font-mono text-[11px] uppercase tracking-widest text-white/40 mb-8">
          <Link to="/" className="hover:text-white">Home</Link> / <Link to="/shop" className="hover:text-white">Shop</Link> / <span className="text-white/70">Grip Socks</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          {/* GALLERY (sticky on desktop) */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <motion.div key={color.key + activeImg} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
              className="rounded-3xl overflow-hidden border border-white/10 aspect-[4/5]">
              <ZoomImage src={gallery[activeImg]} alt={`${PRODUCT.name} – ${color.label}`} />
            </motion.div>
            <div className="mt-4 grid grid-cols-3 gap-4">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  data-testid={`gallery-thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`rounded-xl overflow-hidden border aspect-square transition-all ${activeImg === i ? "border-gold" : "border-white/10 hover:border-white/40"}`}
                >
                  <img src={g} alt={`Ansicht ${i + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* DETAILS */}
          <div>
            <p className="overline mb-3">STARSOX Original</p>
            <h1 className="font-heading text-4xl md:text-6xl uppercase leading-[0.92]">{PRODUCT.name}</h1>

            <div className="flex items-center gap-3 mt-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">Premium Performance</span>
              <span className="h-1 w-1 rounded-full bg-white/30" />
              <span className="text-white/50 text-sm">Grip-Socken · One Size</span>
            </div>

            <div className="mt-6 flex items-baseline gap-3">
              <span data-testid="product-price" className="font-heading text-4xl">{eur(PRODUCT.priceCents * qty)}</span>
              <span className="text-white/40 text-sm">inkl. MwSt. · zzgl. Versand</span>
            </div>

            <p className="mt-6 text-white/60 leading-relaxed max-w-lg">{PRODUCT.tagline} Perfekt für Fußball, Torwarte und jede Sportart mit explosiven Bewegungen.</p>

            {/* Color selector */}
            <div className="mt-9">
              <div className="flex items-center justify-between mb-3">
                <span className="field-label mb-0">Farbe</span>
                <span className="text-sm text-white/70">{color.label}</span>
              </div>
              <div className="flex items-center gap-4">
                {COLORS.map((c) => (
                  <button
                    key={c.key}
                    data-testid={`product-color-${c.key}`}
                    onClick={() => selectColor(c)}
                    aria-label={c.label}
                    className={`relative h-12 w-12 rounded-full transition-all duration-300 ${color.key === c.key ? "ring-2 ring-gold ring-offset-2 ring-offset-ink" : "ring-1 ring-white/20 hover:ring-white/50"}`}
                    style={{ backgroundColor: c.hex }}
                  >
                    {color.key === c.key && c.key === "white" && <Check className="w-5 h-5 text-ink absolute inset-0 m-auto" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Size */}
            <div className="mt-8">
              <span className="field-label">Größe</span>
              <div className="inline-flex items-center gap-3 border border-white/15 rounded-full px-5 py-3">
                <span className="font-semibold">{PRODUCT.size}</span>
                <span className="text-white/40 text-sm">passt {PRODUCT.fits}</span>
              </div>
            </div>

            {/* Quantity + Add */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <div className="flex items-center border border-white/15 rounded-full">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="product-qty-dec" aria-label="Weniger" className="p-4 hover:text-gold transition-colors"><Minus className="w-4 h-4" /></button>
                <span className="w-10 text-center tabular-nums" data-testid="product-qty">{qty}</span>
                <button onClick={() => setQty((q) => Math.min(100, q + 1))} data-testid="product-qty-inc" aria-label="Mehr" className="p-4 hover:text-gold transition-colors"><Plus className="w-4 h-4" /></button>
              </div>
              <button onClick={add} data-testid="product-add-cart" className="btn-primary flex-1 min-w-[220px]">
                In den Warenkorb · {eur(PRODUCT.priceCents * qty)}
              </button>
            </div>

            {/* Trust row */}
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[[Truck, "2–3 Werktage"], [RefreshCcw, "14 Tage Rückgabe"], [ShieldCheck, "Sichere Zahlung"]].map(([Icon, label], i) => (
                <div key={i} className="flex flex-col items-center text-center gap-2 border border-white/10 rounded-xl py-4">
                  <Icon className="w-5 h-5 text-gold" />
                  <span className="text-white/60 text-xs">{label}</span>
                </div>
              ))}
            </div>

            {/* Payment methods */}
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Bezahlen mit</span>
              <PaymentIcons testId="payment-icons-product" />
            </div>

            {/* Features chips */}
            <div className="mt-8 flex flex-wrap gap-2">
              {FEATURES.map((f) => (
                <span key={f} className="font-mono text-[10px] uppercase tracking-widest bg-white/5 border border-white/10 px-3 py-1.5 rounded-full text-white/70">{f}</span>
              ))}
            </div>

            {/* Accordions */}
            <Accordion type="single" collapsible className="mt-10" defaultValue="material">
              <AccordionItem value="material" className="border-white/10">
                <AccordionTrigger className="font-heading text-xl uppercase tracking-wide hover:no-underline">Material</AccordionTrigger>
                <AccordionContent className="text-white/60 leading-relaxed">
                  {PRODUCT.material}. Hochelastisches Performance-Gestrick mit Silikon-Grip-Pads an der Sohle.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="care" className="border-white/10">
                <AccordionTrigger className="font-heading text-xl uppercase tracking-wide hover:no-underline">Pflegehinweise</AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-2 text-white/60">
                    {CARE.map((c) => <li key={c} className="flex gap-2"><Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />{c}</li>)}
                  </ul>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="shipping" className="border-white/10">
                <AccordionTrigger className="font-heading text-xl uppercase tracking-wide hover:no-underline">Versand & Rückgabe</AccordionTrigger>
                <AccordionContent className="text-white/60 leading-relaxed space-y-2">
                  <p>Versand innerhalb Deutschlands: 4,19 € · ab 50 € kostenlos.</p>
                  <p>Lieferzeit: 2–3 Werktage. 14 Tage Rückgaberecht.</p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>

      {/* FAQ snippet */}
      <section className="border-t border-white/10 py-20 md:py-28 bg-ink">
        <div className="container-x max-w-3xl">
          <Reveal>
            <p className="overline mb-4">Häufige Fragen</p>
            <h2 className="font-heading text-3xl md:text-5xl uppercase">Gut zu wissen</h2>
          </Reveal>
          <Accordion type="single" collapsible className="mt-10">
            {FAQS.slice(0, 5).map((f, i) => (
              <AccordionItem key={i} value={`f${i}`} className="border-white/10">
                <AccordionTrigger className="font-heading text-lg md:text-xl uppercase tracking-wide text-left hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-white/60 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <Link to="/faq" data-testid="product-all-faq" className="btn-ghost mt-8">Alle Fragen ansehen <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      {/* Mobile sticky add-to-cart */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-ink/90 backdrop-blur-xl border-t border-white/10 px-4 py-3 flex items-center gap-3">
        <div className="shrink-0">
          <p className="text-xs text-white/50">{color.label}</p>
          <p className="font-heading text-lg leading-none">{eur(PRODUCT.priceCents * qty)}</p>
        </div>
        <button onClick={add} data-testid="product-add-cart-mobile" className="btn-primary flex-1">In den Warenkorb</button>
      </div>
    </div>
  );
}
